import type { ResolvedDataSource } from '@de/providers/DataSourceManager.types';
import { getByPath, parse, InterpolationError } from '@de/utils/interpolation';
import type { PathSegment } from '@de/utils/interpolation/types';
import type { BoundComponentInfo } from './findBoundComponent';

/**
 * Resolve the canonical `slug` for a `formKey` selection from the
 * bound data source.
 *
 * Why this is its own helper (not folded into `resolveDisplayLabel`):
 * DSL authors bind `_bind.optionValueField` to the field downstream
 * **cascading queries** need (Mobius's `criteria.degreeId` is int64
 * `JSONArrayableInt64`, so the value has to be the numeric `id`), but
 * DSL **branching conditions** (`COMBINATION_SELECTED.combination`)
 * are keyed on slugs (`'bachelors'`, `'nursing-and-healthcare'`).
 * The bridge needs to surface BOTH on the committed `Option`:
 *   - `value` (whatever was bound, used by `formState[formKey]`)
 *   - `slug`  (always the source item's `slug` field when present,
 *              used by `evaluateCondition` for combination matching)
 *
 * Algorithm mirrors {@link resolveDisplayLabel}:
 *   1. Reach `source.data` via `bound.optionsPath` using the same
 *      interpolation grammar `applyOptions` uses to render the
 *      dropdown — so we look up the same entries the user picked
 *      from.
 *   2. Deep-flatten arrays-of-arrays produced by `[*]` selectors.
 *   3. Find the entry whose `[optionValueField] === value` and
 *      return `entry.slug` as a string. Return `undefined` on any
 *      miss (no bound info, unresolved source, malformed path,
 *      missing slug field, no match) so the caller can leave
 *      `Option.slug` unset rather than emit a fake slug.
 *
 * Cascading "first wins" caveat — same as `resolveDisplayLabel`:
 * for cascading paths like `degrees[*].categories`, deep-flatten
 * can surface entries that share the same `optionValueField` across
 * different parents (e.g. two distinct degrees each containing a
 * category with `id: 13`). This helper returns the **first** match
 * encountered in the flattened order. That's safe today because
 * Mobius's cascading queries are pre-filtered server-side by the
 * parent selection (`criteria.degreeId`), so the response only
 * contains the categories valid under the picked degree — the
 * "first" match is the only match. If a future data source returns
 * a globally-flat list with shared ids, callers must either
 * pre-filter the source or extend `_bind.optionsPath` to scope the
 * lookup. `resolveSourceSlug.test.ts` pins this behavior so the
 * assumption is discoverable.
 *
 * Pure function. No console output -- `resolveDisplayLabel` already
 * emits the diagnostic for the same misconfigurations, and a missing
 * slug field is a perfectly valid shape for non-Mobius data sources.
 */
export function resolveSourceSlug(
  value: string,
  bound: BoundComponentInfo | undefined,
  source: ResolvedDataSource | undefined,
): string | undefined {
  if (!bound?.optionValueField) return undefined;
  const sourceData = source?.data;
  if (sourceData === undefined || sourceData === null) return undefined;

  const items = collectItems(sourceData, bound.optionsPath);
  if (items.length === 0) return undefined;

  const valueField = bound.optionValueField;
  for (let i = 0; i < items.length; i++) {
    const entry = items[i];
    if (entry && typeof entry === 'object') {
      const candidate = (entry as Record<string, unknown>)[valueField];
      if (candidate !== undefined && String(candidate) === value) {
        const slug = (entry as Record<string, unknown>).slug;
        if (typeof slug === 'string' && slug.length > 0) {
          return slug;
        }
        return undefined;
      }
    }
  }
  return undefined;
}

/**
 * Pull the option-list array out of a source payload, applying the
 * same bind-path traversal + deep-flatten the renderer used. Kept
 * private here (rather than imported from `resolveDisplayLabel`) so
 * the two resolvers can evolve their failure modes independently
 * without one accidentally swallowing the other's warn-once latch.
 */
function collectItems(
  data: unknown,
  optionsPath: string | undefined,
): readonly unknown[] {
  if (optionsPath === undefined) {
    return Array.isArray(data) ? flatten(data) : [];
  }
  let segments: ReadonlyArray<PathSegment> | undefined;
  try {
    const parsed = parse(`{{${optionsPath}}}`);
    const expr = parsed.segments[0];
    if (expr && expr.type === 'expression' && expr.kind === 'path') {
      segments = expr.segments;
    }
  } catch (err) {
    if (err instanceof InterpolationError) {
      return [];
    }
    throw err;
  }
  if (!segments || segments.length === 0) return [];
  const reached = getByPath(data, segments);
  if (!Array.isArray(reached)) return [];
  return flatten(reached);
}

function flatten(input: ReadonlyArray<unknown>): unknown[] {
  const out: unknown[] = [];
  const visit = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) visit(v[i]);
      return;
    }
    out.push(v);
  };
  visit(input);
  return out;
}
